import React, { useState, useEffect } from 'react';
import { VideoClip } from '../../../../containers/PracticeManagementGroups/Conferences/SaveVideoClipPopup/SaveVideoClipPopup';
import VideoClipTile from '../../../../containers/PracticeManagementGroups/Conferences/VideoClipTile/VideoClipTile';
import VideoPlayerPopup from '../../../VideoPlayerPopup/VideoPlayerPopup';
import Helpers from '../../../../utils/helper';
import { useStateSelector } from '../../../../store/selectors';
import createNotification from '../../../../utils/createNotification';
import {
    TeamMemberType,
    InternalPermissions,
    ExternalPermissions,
} from '../../../Admin/BuyerMarketplace/MyTeamMembers/Models/Enums';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';

interface EngagementVideoClipsListProps {
    engagementId: number;
    areMyClipsOnlyVisible: boolean;
    loadingTrigger: number;
    onClipSave: () => void;
    isReadOnlyMode?: boolean;
    customApiEndpoint?: string;
    onVideoClipOpen?: (clip: VideoClip, source: string, isHls: boolean) => void;
}

const EngagementVideoClipsList = (props: EngagementVideoClipsListProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [clips, setClips] = useState<VideoClip[]>([]);
    const [selectedClip, setSelectedClip] = useState<VideoClip>();
    const [videoSource, setVideoSource] = useState<string>();
    const [videoIsHls, setVideoIsHls] = useState<boolean>(false);
    const axios = useStateSelector((state) => state.core.axios);
    const selectedOpportunity = useStateSelector(
        (state: any) => state.opportunities?.selectedOpportuity
    );
    const party = useCurrentMarketplaceParty();

    const isShareButtonVisible = () => {
        if (
            !selectedOpportunity ||
            party !== TimelineParty.Buyer ||
            selectedOpportunity.isPrimaryBuyer
        ) {
            return true;
        }

        if (selectedOpportunity.teamMemberType === TeamMemberType.Internal) {
            return (
                selectedOpportunity.internalPermissions ===
                    InternalPermissions.ReadOnlyAndCommunicate ||
                selectedOpportunity.internalPermissions ===
                    InternalPermissions.ReadOnlyAndCommunicateAndExecute
            );
        } else if (
            selectedOpportunity.teamMemberType === TeamMemberType.External
        ) {
            return (
                selectedOpportunity.externalPermissions ===
                    ExternalPermissions.VdrAndCommunicate ||
                selectedOpportunity.externalPermissions ===
                    ExternalPermissions.VdrAndReadOnlyCommunicate
            );
        }

        return true;
    };

    useEffect(() => {
        loadClips();
    }, []);

    useEffect(() => {
        if (!isLoading) {
            loadClips();
        }
    }, [props.loadingTrigger]);

    const loadClips = async () => {
        setIsLoading(true);
        try {
            const endpoint =
                props.customApiEndpoint ||
                (props.areMyClipsOnlyVisible
                    ? `/api/Marketplace/Engagements/${props.engagementId}/EngagementMeetings/clips`
                    : `/api/Marketplace/Engagements/${props.engagementId}/EngagementMeetings/clips/shared`);

            const response = await axios.get(endpoint);
            if (response.status === 200) {
                setClips(response.data || []);
            } else {
                setClips([]);
                createNotification('Error loading video clips', 'error');
            }
        } catch (error) {
            setClips([]);
            createNotification('Error loading video clips', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleShareStatusChange = async (
        clipId: number,
        isShared: boolean,
        conferenceId: number
    ) => {
        try {
            const endpoint = props.customApiEndpoint
                ? `/api/Marketplace/MarketplaceMeetings/${conferenceId}/clips/${clipId}/sharingStatus`
                : `/api/Marketplace/Engagements/${props.engagementId}/EngagementMeetings/${conferenceId}/clips/${clipId}/sharingStatus`;
            const body = {
                ConferenceId: conferenceId,
                IsShared: isShared,
            };

            const response = await axios.post(endpoint, body);
            if (response.status === 200) {
                createNotification(
                    `Clip ${isShared ? 'shared' : 'unshared'} successfully`,
                    'success'
                );
                props.onClipSave();
            } else {
                createNotification(
                    'Error updating clip sharing status',
                    'error'
                );
            }
        } catch (error) {
            createNotification('Error updating clip sharing status', 'error');
        }
    };

    const fetchClipSource = async (clip: VideoClip, onComplete: () => void) => {
        if (props.onVideoClipOpen) {
            // New behavior: use callback for parent to handle video player
            if (!clip) {
                onComplete();
                return;
            }

            try {
                const endpoint = props.customApiEndpoint
                    ? `/api/Marketplace/MarketplaceMeetings/${clip.conferenceId}/clips/${clip.id}`
                    : `/api/Marketplace/Engagements/${props.engagementId}/EngagementMeetings/${clip.conferenceId}/clips/${clip.id}`;
                const response = await axios.get(endpoint);

                if (response.status === 200) {
                    props.onVideoClipOpen(clip, response.data.url, response.data.isHls);
                } else {
                    createNotification('Error loading video clip', 'error');
                }
            } catch (error) {
                createNotification('Error loading video clip', 'error');
            } finally {
                onComplete();
            }
        } else {
            // Original behavior: handle video player locally
            setSelectedClip(clip);
            if (!clip) {
                onComplete();
                return;
            }

            try {
                const endpoint = props.customApiEndpoint
                    ? `/api/Marketplace/MarketplaceMeetings/${clip.conferenceId}/clips/${clip.id}`
                    : `/api/Marketplace/Engagements/${props.engagementId}/EngagementMeetings/${clip.conferenceId}/clips/${clip.id}`;
                const response = await axios.get(endpoint);

                if (response.status === 200) {
                    setVideoSource(response.data.url);
                    setVideoIsHls(response.data.isHls);
                } else {
                    createNotification('Error loading video clip', 'error');
                }
            } catch (error) {
                createNotification('Error loading video clip', 'error');
            } finally {
                onComplete();
            }
        }
    };

    return (
        <>
            {isLoading ? (
                Helpers.renderTableLoader()
            ) : (
                <div className="conferences-wrapper">
                    {clips?.length === 0 ? (
                        <span className="no-data-label">No Data available</span>
                    ) : (
                        []
                    )}
                    {clips.map((clip, index) => {
                        return (
                            <div key={'clip-' + index}>
                                <VideoClipTile
                                    isShared={!props.areMyClipsOnlyVisible}
                                    engagementId={
                                        props.customApiEndpoint
                                            ? undefined
                                            : props.engagementId
                                    }
                                    clip={clip}
                                    shareCheckboxText={
                                        'Share this clip with meeting participants'
                                    }
                                    isShareButtonHidden={
                                        props.isReadOnlyMode || !isShareButtonVisible()
                                    }
                                    onShareCheckboxChange={(
                                        clipId: number,
                                        isShared: boolean
                                    ) => {
                                        handleShareStatusChange(
                                            clipId,
                                            isShared,
                                            clip.conferenceId
                                        );
                                    }}
                                    onViewRecording={
                                        props.isReadOnlyMode
                                            ? undefined
                                            : (
                                                  clip: VideoClip,
                                                  onComplete: () => void
                                              ) => {
                                                  fetchClipSource(
                                                      clip,
                                                      onComplete
                                                  );
                                              }
                                    }
                                    onStatusChanged={(
                                        clipId: number,
                                        isShared: boolean
                                    ) => {
                                        console.log(
                                            'Status changed:',
                                            clipId,
                                            isShared
                                        );
                                        props.onClipSave();
                                    }}
                                    onClipUpdated={() => {
                                        loadClips();
                                    }}
                                    type={
                                        props.customApiEndpoint
                                            ? 'marketplace-meetings'
                                            : 'engagement-meetings'
                                    }></VideoClipTile>
                            </div>
                        );
                    })}
                </div>
            )}
            {!props.onVideoClipOpen && selectedClip && videoSource && (
                <VideoPlayerPopup
                    title={selectedClip.title}
                    src={videoSource}
                    isHls={videoIsHls}
                    onClose={() => {
                        setVideoSource(undefined);
                        setSelectedClip(undefined);
                    }}
                    isRecordingEnabled={false}
                    startTimeSeconds={selectedClip.timeStartSeconds}
                    endTimeSeconds={selectedClip.timeEndSeconds}
                />
            )}
        </>
    );
};

export default EngagementVideoClipsList;
